import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Landmark,
  Users,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Plus,
  RefreshCw,
  Lock,
  ScrollText,
  Info,
  Sparkles,
  Settings,
} from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import {
  PensionCoverageView,
  GpssaRemittanceView,
  MonthlyPensionContributionView,
  GpssaComplianceSummaryView,
  GpssaContributionSkipView,
} from '../../../../models/payroll';
import { generateContributions, approvePendingContributions } from '../../../../api/Payroll/gpssa';
import { GpssaAdpfSplitTable } from './GpssaAdpfSplitTable';
import { ComplianceStatusCard } from './ComplianceStatusCard';
import { LawReference } from './LawReference';

export interface UaePensionPanelProps {
  year: number;
  month: number;
  coverage: PensionCoverageView[];
  remittance: GpssaRemittanceView | null;
  contributions: MonthlyPensionContributionView[];
  summary: GpssaComplianceSummaryView | null;
  /** True when exactly one CompanyGrossPay component is flagged IsBasicSalary. */
  basicSalaryHealthy?: boolean;
  /** Count of components flagged IsBasicSalary (0 = missing, >1 = ambiguous). */
  basicSalaryConfigCount?: number;
  onContributionsGenerated: (items: MonthlyPensionContributionView[]) => void;
  onError: (msg: string) => void;
  onInfo?: (msg: string) => void;
}

export const UaePensionPanel: React.FC<UaePensionPanelProps> = ({
  year,
  month,
  coverage,
  remittance,
  contributions,
  summary,
  basicSalaryHealthy,
  basicSalaryConfigCount,
  onContributionsGenerated,
  onError,
  onInfo,
}) => {
  const { t } = useTranslation();
  const [generating, setGenerating] = useState(false);
  const [skipped, setSkipped] = useState<GpssaContributionSkipView[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setSkipped([]);
  }, [year, month]);

  const hasExistingContributions = contributions.length > 0;
  const lockedByPayroll = contributions.some(c => c.isIncludedInPayroll);
  const pendingCount = contributions.filter(c => c.isPending).length;
  const [approving, setApproving] = useState(false);

  // Hard gate: if the company has no (or multiple) IsBasicSalary components, every
  // contribution will be skipped (CompanyBasicComponentMissing). Block the button
  // and point HR straight at Pay Components, instead of letting them produce another
  // skip event in the audit trail.
  const basicSalaryBlocked = basicSalaryHealthy === false;
  const noBasicSalaryConfigured = basicSalaryBlocked && (basicSalaryConfigCount ?? 0) === 0;

  const handleApprovePending = async () => {
    setApproving(true);
    try {
      const result = await approvePendingContributions(year, month);
      if (result.errors.length === 0) {
        onInfo?.(
          t('payroll.gpssa.approvedNote', 'Approved {{count}} pending contribution(s).', {
            count: result.payload ?? 0,
          })
        );
        // Re-fetch by triggering parent refresh: easiest is to surface the change via the
        // existing onContributionsGenerated hook. We pass an empty list here only to nudge —
        // the parent's fetchData will repopulate authoritative state.
        onContributionsGenerated(contributions.map(c => ({ ...c, isPending: false })));
      } else {
        onError(result.errors[0].message);
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setApproving(false);
    }
  };

  const runGenerate = async () => {
    setGenerating(true);
    setConfirmOpen(false);
    try {
      const result = await generateContributions(year, month);
      const payload = result.payload;

      if (result.errors.length === 0 && payload) {
        setSkipped(payload.skipped ?? []);
        onContributionsGenerated(payload.contributions ?? []);

        const generated = payload.contributions?.length ?? 0;
        const skippedCount = payload.skipped?.length ?? 0;
        const enrolled = payload.enrolledCount ?? 0;

        const parts: string[] = [];
        if (payload.regenerated) {
          parts.push(t('payroll.gpssa.regeneratedNote', 'Previous contributions for this period were replaced.'));
        }
        if (enrolled > 0) {
          parts.push(
            t('payroll.gpssa.autoEnrolledNote', '{{count}} UAE national(s) were auto-enrolled.', { count: enrolled })
          );
        }
        if (generated > 0) {
          parts.push(
            t('payroll.gpssa.generatedNote', '{{count}} contribution(s) generated.', { count: generated })
          );
        }
        if (skippedCount > 0 && generated === 0) {
          parts.push(
            t(
              'payroll.gpssa.allSkippedNote',
              'No contributions were generated. See the warnings below for what to fix.'
            )
          );
        } else if (skippedCount > 0) {
          parts.push(
            t('payroll.gpssa.partialSkippedNote', '{{count}} employee(s) were skipped. See warnings below.', {
              count: skippedCount,
            })
          );
        }
        onInfo?.(parts.join(' '));
      } else {
        // Backend may return a 400 with payload.lockedByPayroll = true.
        if (payload?.lockedByPayroll) {
          onContributionsGenerated(payload.contributions ?? []);
        }
        onError(result.errors[0]?.message ?? t('common.error'));
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setGenerating(false);
    }
  };

  const handleClick = () => {
    if (lockedByPayroll) return;
    if (basicSalaryBlocked) return;
    if (hasExistingContributions) {
      setConfirmOpen(true);
      return;
    }
    runGenerate();
  };

  const buttonLabel = generating
    ? t('common.generating', 'Generating...')
    : lockedByPayroll
    ? t('payroll.gpssa.lockedButton', 'Locked (Already in Payroll)')
    : basicSalaryBlocked
    ? t('payroll.gpssa.basicSalaryBlockedButton', 'Configure Basic Salary first')
    : hasExistingContributions
    ? t('payroll.gpssa.regenerateContributions', 'Regenerate Contributions')
    : t('payroll.gpssa.generateContributions', 'Generate Contributions');

  const ButtonIcon = lockedByPayroll
    ? Lock
    : basicSalaryBlocked
    ? Settings
    : hasExistingContributions
    ? RefreshCw
    : Plus;

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 flex items-start gap-3">
        <ScrollText className="w-5 h-5 mt-0.5 shrink-0 text-blue-600" />
        <div className="flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="font-semibold">
              {t('payroll.complianceCenter.pension.lawBannerTitle', 'Pension Coverage for UAE Nationals')}
            </p>
            <LawReference
              citations={[
                {
                  code: 'GPSSA: Federal Law 7/1999 (amended by FDL 57/2023)',
                  description: t(
                    'payroll.complianceCenter.lawRef.gpssa',
                    'UAE nationals in the private sector outside Abu Dhabi are entitled to GPSSA pension coverage. Post-2023 hires use the higher employee contribution rate of 11%.'
                  ),
                },
                {
                  code: 'ADPF: Abu Dhabi Law 2/2000',
                  description: t(
                    'payroll.complianceCenter.lawRef.adpf',
                    'Abu Dhabi Pension Fund covers UAE nationals working in Abu Dhabi private sector with separate contribution schedules.'
                  ),
                },
              ]}
            />
          </div>
          <p className="text-sm text-blue-800/90 mt-1">
            {t(
              'payroll.complianceCenter.pension.lawBannerBody',
              'UAE nationals in the private sector are entitled to pension coverage per GPSSA Federal Law No. 7 of 1999 (amended by Federal Decree-Law No. 57 of 2023) and Abu Dhabi Pension Fund Law No. 2 of 2000.'
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ComplianceStatusCard
          icon={Users}
          label={t('payroll.gpssa.eligibleEmployees', 'UAE Nationals')}
          value={summary?.totalUaeNationals ?? 0}
        />
        <ComplianceStatusCard
          icon={CheckCircle2}
          label={t('payroll.gpssa.covered', 'Covered')}
          value={summary?.coveredCount ?? 0}
          tone="green"
        />
        <ComplianceStatusCard
          icon={AlertTriangle}
          label={t('payroll.gpssa.gaps', 'Gaps')}
          value={summary?.gapCount ?? 0}
          tone={(summary?.gapCount ?? 0) > 0 ? 'red' : 'green'}
        />
        <ComplianceStatusCard
          icon={Landmark}
          label={t('payroll.gpssa.status', 'Status')}
          value={
            summary?.fullyCovered
              ? t('payroll.gpssa.fullyCovered', 'Covered')
              : t('payroll.gpssa.hasGaps', 'Gaps')
          }
          tone={summary?.fullyCovered ? 'green' : 'amber'}
        />
      </div>

      {summary && (
        <div
          className={`p-4 rounded-lg border ${
            summary.fullyCovered
              ? 'bg-green-50 border-green-200 text-green-700'
              : 'bg-amber-50 border-amber-200 text-amber-700'
          }`}
        >
          <div className="flex items-center gap-2">
            {summary.fullyCovered ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertTriangle className="w-5 h-5" />
            )}
            <span className="font-medium">
              {summary.fullyCovered
                ? t('payroll.gpssa.allEnrolled', 'All eligible UAE nationals are covered by pension.')
                : t(
                    'payroll.gpssa.missingEnrollment',
                    '{{count}} eligible employees are not yet covered.',
                    { count: summary.gapCount }
                  )}
            </span>
          </div>
        </div>
      )}

      {basicSalaryBlocked && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">
              {noBasicSalaryConfigured
                ? t(
                    'payroll.gpssa.basicSalaryMissingTitle',
                    'Basic Salary component is not configured'
                  )
                : t(
                    'payroll.gpssa.basicSalaryAmbiguousTitle',
                    'Basic Salary configuration is ambiguous'
                  )}
            </p>
            <p className="text-sm mt-1">
              {noBasicSalaryConfigured
                ? t(
                    'payroll.gpssa.basicSalaryMissingBody',
                    'No gross-pay component is flagged as "Is Basic Salary". Pension contributions cannot be computed without a basic-salary anchor. Every employee would be skipped. Flag exactly one component before generating.'
                  )
                : t(
                    'payroll.gpssa.basicSalaryAmbiguousBody',
                    '{{count}} components are flagged as "Is Basic Salary". Exactly one is required (FDL 33/2021 Art. 19/29/51). Fix the configuration before generating.',
                    { count: basicSalaryConfigCount ?? 0 }
                  )}
            </p>
            <Link
              to="/settings/payroll/grosspays"
              className="inline-flex items-center gap-1.5 text-sm font-medium underline mt-2"
            >
              <Settings className="w-3.5 h-3.5" />
              {t('payroll.gpssa.fixInPayComponents', 'Fix in Pay Components')}
            </Link>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleClick}
          disabled={generating || lockedByPayroll || basicSalaryBlocked}
          className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            lockedByPayroll
              ? 'bg-slate-100 border-slate-300 text-slate-600'
              : basicSalaryBlocked
              ? 'bg-slate-100 border-slate-300 text-slate-600'
              : hasExistingContributions
              ? 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100'
              : 'bg-blue-100 border-blue-300 text-blue-800 hover:bg-blue-200'
          }`}
          title={
            lockedByPayroll
              ? t(
                  'payroll.gpssa.lockedTooltip',
                  'These contributions have already been included in monthly payroll and cannot be regenerated.'
                )
              : basicSalaryBlocked
              ? t(
                  'payroll.gpssa.basicSalaryBlockedTooltip',
                  'Disabled. Configure exactly one Basic Salary component in Pay Components before generating.'
                )
              : undefined
          }
        >
          <ButtonIcon className="w-4 h-4" />
          {buttonLabel}
        </button>
        {lockedByPayroll && (
          <span className="text-xs text-slate-600 inline-flex items-center gap-1">
            <Info className="w-3.5 h-3.5" />
            {t(
              'payroll.gpssa.lockedHint',
              'Pension is once-monthly. Run was already pulled into payroll.'
            )}
          </span>
        )}
      </div>

      {pendingCount > 0 && !lockedByPayroll && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-amber-900">
                {t(
                  'payroll.gpssa.pendingTitle',
                  '{{count}} contribution(s) waiting for approval',
                  { count: pendingCount }
                )}
              </p>
              <p className="text-sm text-amber-800/90 mt-0.5">
                {t(
                  'payroll.gpssa.pendingBody',
                  'These were generated automatically (Auto-Preview mode). Review the figures below, then approve so monthly payroll can consume them.'
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleApprovePending}
            disabled={approving}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-amber-600 text-white text-sm hover:bg-amber-700 disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4" />
            {approving
              ? t('common.processing', 'Processing...')
              : t('payroll.gpssa.approvePending', 'Approve pending contributions')}
          </button>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-amber-200 text-amber-900">
            <AlertTriangle className="w-4 h-4" />
            <p className="text-sm font-semibold">
              {t(
                'payroll.gpssa.skippedHeader',
                '{{count}} employee(s) were skipped during generation',
                { count: skipped.length }
              )}
            </p>
          </div>
          <ul className="divide-y divide-amber-200">
            {skipped.map(s => (
              <li key={s.userId} className="px-4 py-2.5 text-sm flex flex-col gap-0.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-amber-900">{s.employeeName}</span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      s.scheme.toUpperCase() === 'GPSSA'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}
                  >
                    {s.scheme}
                  </span>
                </div>
                <span className="text-xs text-amber-800/90">{s.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <GpssaAdpfSplitTable
        contributions={contributions}
        gpssaEnrolledCount={
          coverage.filter(c => (c.scheme ?? '').toUpperCase() === 'GPSSA').length
        }
        adpfEnrolledCount={
          coverage.filter(c => (c.scheme ?? '').toUpperCase() === 'ADPF').length
        }
        gpssaSkippedCount={
          skipped.filter(s => (s.scheme ?? '').toUpperCase() === 'GPSSA').length
        }
        adpfSkippedCount={
          skipped.filter(s => (s.scheme ?? '').toUpperCase() === 'ADPF').length
        }
      />

      {remittance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('payroll.gpssa.gpssaTotal', 'GPSSA Total')}
              </p>
              <p className="text-2xl font-bold font-mono">
                {(remittance.gpssaTotal ?? 0).toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">
                {remittance.gpssaCount} {t('common.employees', 'employees')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('payroll.gpssa.adpfTotal', 'ADPF Total')}
              </p>
              <p className="text-2xl font-bold font-mono">
                {(remittance.adpfTotal ?? 0).toFixed(2)}
              </p>
              <p className="text-xs text-muted-foreground">
                {remittance.adpfCount} {t('common.employees', 'employees')}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('payroll.gpssa.grandTotal', 'Grand Total')}
              </p>
              <p className="text-2xl font-bold font-mono">
                {(remittance.grandTotal ?? 0).toFixed(2)}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-1">
            {t('payroll.gpssa.employeeCoverage', 'Employee Pension Coverage')}
          </h3>
          <p className="text-xs text-muted-foreground mb-3">
            {t(
              'payroll.gpssa.coverageScope',
              'Only UAE nationals are listed. Pension coverage applies under GPSSA / ADPF only.'
            )}
          </p>
          {coverage.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t('payroll.gpssa.noCoverage', 'No UAE national employees found in this company.')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-start py-2 font-medium">
                      {t('common.employee', 'Employee')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.gpssa.nationality', 'Nationality')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.gpssa.scheme', 'Scheme')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.gpssa.enrolled', 'Enrolled')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.gpssa.employeeRate', 'Emp. Rate')}
                    </th>
                    <th className="text-end py-2 font-medium">
                      {t('payroll.gpssa.employerRate', 'Empl. Rate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {coverage.map(emp => (
                    <tr key={emp.userId} className="border-b border-border last:border-0">
                      <td className="py-2">{emp.employeeName}</td>
                      <td className="py-2">{emp.nationality ?? '-'}</td>
                      <td className="py-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            (emp.scheme ?? '').toUpperCase() === 'GPSSA'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {emp.scheme ?? '-'}
                        </span>
                      </td>
                      <td className="py-2">
                        {emp.isRegistered ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                      </td>
                      <td className="py-2 text-end font-mono">
                        {Number((emp.employeeRate * 100).toFixed(2))}%
                      </td>
                      <td className="py-2 text-end font-mono">
                        {Number((emp.employerRate * 100).toFixed(2))}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-amber-600" />
              <h3 className="font-semibold">
                {t('payroll.gpssa.regenerateConfirmTitle', 'Regenerate contributions?')}
              </h3>
            </div>
            <div className="px-5 py-4 text-sm text-muted-foreground space-y-2">
              <p>
                {t(
                  'payroll.gpssa.regenerateConfirmBody',
                  'Contributions for this period already exist. Regenerating will archive the current rows and recompute them from each employee\'s current basic salary and active pension record.'
                )}
              </p>
              <p>
                {t(
                  'payroll.gpssa.regenerateConfirmTip',
                  'Pension is paid once a month. Only regenerate if a setup error needs correcting before payroll consumes these values.'
                )}
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-muted"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={runGenerate}
                className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700"
              >
                {t('payroll.gpssa.regenerateConfirmAction', 'Yes, regenerate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
